'use client';

import GlobeOutlineIconSvg from '@/assets/svgs/globe-outline.svg';
import OptOutsIconSvg from '@/assets/svgs/main/opt-outs.svg';
import PencilIconSvg from '@/assets/svgs/pencil.svg';
import PhoneIconSvg from '@/assets/svgs/phone.svg';
import UploadIcon from '@/assets/svgs/text-editor/upload.svg';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Modals } from '../all-modals';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { AddOptOutModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import { AreaCodeTab } from './AreaCodeTab';
import { FileUploadTab } from './FileUploadTab';
import { ManualTab } from './ManualTab';
import { StateTab } from './StateTab';

const modalName = Modals.AddOptOutModal;

interface AddOptOutModalProps extends ModalProps {
  params?: AddOptOutModalParams;
}

type TabType = 'manual' | 'file' | 'state' | 'area-code';

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
      {
        'border-primary text-primary dark:border-white dark:text-white': active,
        'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700':
          !active,
      }
    )}>
    <div className="h-4 w-4">{icon}</div>
    {children}
  </button>
);

const AddOptOutModal = ({ variant = 'narrow', params }: AddOptOutModalProps) => {
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  const [activeTab, setActiveTab] = useState<TabType>('manual');

  // Extract params
  const { accountId = '', identities = [], onSuccess } = params || {};

  const showIdentitySelector = identities.length > 1;

  if (!isModalOpen) return null;

  return (
    <div className="relative">
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<OptOutsIconSvg className="h-5 w-5" />}
            title="Add new opt-out"
          />

          {/* Body */}
          <ModalBody className="flex-1 px-5 !py-0">
            {/* Spacing for consistency */}
            <div className="h-6" />

            {/* Description */}
            <div className="mb-6">
              <p className="text-gray-500">Select & Fill details below add new opt-out</p>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6 flex border-b">
              <TabButton
                active={activeTab === 'manual'}
                onClick={() => setActiveTab('manual')}
                icon={<PencilIconSvg className="h-4 w-4" />}>
                Manual
              </TabButton>
              <TabButton
                active={activeTab === 'file'}
                onClick={() => setActiveTab('file')}
                icon={<UploadIcon />}>
                File Upload
              </TabButton>
              <TabButton
                active={activeTab === 'state'}
                onClick={() => setActiveTab('state')}
                icon={<GlobeOutlineIconSvg className="h-4 w-4" />}>
                State
              </TabButton>
              <TabButton
                active={activeTab === 'area-code'}
                onClick={() => setActiveTab('area-code')}
                icon={<PhoneIconSvg className="h-4 w-4" />}>
                Area Code
              </TabButton>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-1 pb-4">
              {activeTab === 'manual' && (
                <ManualTab
                  accountId={accountId}
                  identities={identities}
                  showIdentitySelector={showIdentitySelector}
                  onSuccess={onSuccess}
                  onClose={onClose}
                />
              )}
              {activeTab === 'file' && (
                <FileUploadTab
                  accountId={accountId}
                  identities={identities}
                  showIdentitySelector={showIdentitySelector}
                  onSuccess={onSuccess}
                  onClose={onClose}
                />
              )}
              {activeTab === 'state' && (
                <StateTab
                  accountId={accountId}
                  identities={identities}
                  showIdentitySelector={showIdentitySelector}
                  onSuccess={onSuccess}
                  onClose={onClose}
                />
              )}
              {activeTab === 'area-code' && (
                <AreaCodeTab
                  accountId={accountId}
                  identities={identities}
                  showIdentitySelector={showIdentitySelector}
                  onSuccess={onSuccess}
                  onClose={onClose}
                />
              )}
            </div>
          </ModalBody>
        </div>
      </ModalContainer>
    </div>
  );
};

export default AddOptOutModal;
